import React from "react";
import { Button, TextField, Typography, Container, Card, CardContent, Box, Link } from "@mui/material";
import styled from "@emotion/styled";
import { useBrandName } from "../utils/brandUtils";

const StyledContainer = styled(Container)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    width: "100%",
    minHeight: "100vh",
    [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(0),
    },
}));

const WideStyledCard = styled(Card)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[3],
    [theme.breakpoints.up("sm")]: {
        width: "750px",
        margin: "0 auto",
    },
    [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(3),
    },
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(3),
}));

const StyledCopyright = styled(Typography)(({ theme }) => ({
    marginTop: theme.spacing(3),
}));

const NavigationButtons = styled(Box)(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(2),
    width: "100%",
}));

const StyledCardRow = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: theme.spacing(1),
    width: "100%",
    gap: theme.spacing(1),
}));

const StyledTextField = styled(TextField, { shouldForwardProp: (prop) => prop !== "error" })(({ theme, error }) => ({
    margin: theme.spacing(1, 0),
    ...(error && {
        "& .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.error.main,
        },
        "& .MuiInputLabel-root": {
            color: theme.palette.error.main,
        },
        "& .MuiInputLabel-shrink": {
            color: theme.palette.error.main,
        },
    }),
}));

const Copyright = () => {
    const brandName = useBrandName();

    return (
        <StyledCopyright variant="body2" color="textSecondary" align="center">
            {"Copyright © "}
            <Link color="inherit" href="https://pollux-mui.com/">
                {brandName}
            </Link>{" "}
            {new Date().getFullYear()}
            {"."}
        </StyledCopyright>
    );
};

const RegisterStep2 = ({ newRegistrationStep1, newRegistrationStep3, formData, setFormData, formErrors, setFormErrors, validateField }) => {
    const handleFormChange = (event) => {
        const { name, value } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleBlur = (event) => {
        const { name, value } = event.target;
        const error = validateField(name, value);
        if (error) {
            setFormErrors((prev) => ({
                ...prev,
                [name]: error,
            }));
        } else {
            setFormErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    return (
        <StyledContainer component="main">
            <WideStyledCard>
                <StyledCardContent component="form">
                    <Typography variant="h4" component="h1" gutterBottom>
                        Company Information
                    </Typography>

                    <Typography variant="body1" color="text.secondary" paragraph align="center">
                        Please provide your company details to continue with registration.
                    </Typography>

                    <StyledTextField
                        fullWidth
                        label="Street Address"
                        id="address"
                        name="address"
                        variant="outlined"
                        value={formData.address}
                        onChange={handleFormChange}
                        onBlur={handleBlur}
                        error={Boolean(formErrors.address)}
                        helperText={formErrors.address || ""}
                    />

                    <StyledCardRow>
                        <StyledTextField
                            fullWidth
                            label="City"
                            id="city"
                            name="city"
                            variant="outlined"
                            value={formData.city}
                            onChange={handleFormChange}
                            onBlur={handleBlur}
                            error={Boolean(formErrors.city)}
                            helperText={formErrors.city || ""}
                        />
                        <StyledTextField
                            fullWidth
                            label="State/Province"
                            id="state"
                            name="state"
                            variant="outlined"
                            value={formData.state}
                            onChange={handleFormChange}
                            onBlur={handleBlur}
                            error={Boolean(formErrors.state)}
                            helperText={formErrors.state || ""}
                        />
                    </StyledCardRow>

                    <StyledCardRow>
                        <StyledTextField
                            fullWidth
                            label="Postal/Zip Code"
                            id="zip"
                            name="zip"
                            variant="outlined"
                            value={formData.zip}
                            onChange={handleFormChange}
                            onBlur={handleBlur}
                            error={Boolean(formErrors.zip)}
                            helperText={formErrors.zip || ""}
                        />
                        <StyledTextField
                            fullWidth
                            label="Country"
                            id="country"
                            name="country"
                            variant="outlined"
                            value={formData.country}
                            onChange={handleFormChange}
                            onBlur={handleBlur}
                            error={Boolean(formErrors.country)}
                            helperText={formErrors.country || ""}
                        />
                    </StyledCardRow>

                    <StyledCardRow>
                        <StyledTextField
                            fullWidth
                            label="Company Email"
                            id="email"
                            name="email"
                            type="email"
                            variant="outlined"
                            value={formData.email}
                            onChange={handleFormChange}
                            onBlur={handleBlur}
                            error={Boolean(formErrors.email)}
                            helperText={formErrors.email || ""}
                        />
                        <StyledTextField
                            fullWidth
                            label="Phone Number"
                            id="phone"
                            name="phone"
                            type="tel"
                            variant="outlined"
                            value={formData.phone}
                            onChange={handleFormChange}
                            onBlur={handleBlur}
                            error={Boolean(formErrors.phone)}
                            helperText={formErrors.phone || ""}
                        />
                    </StyledCardRow>

                    <StyledTextField
                        fullWidth
                        label="Company Website"
                        id="website"
                        name="website"
                        type="url"
                        variant="outlined"
                        placeholder="https://"
                        value={formData.website}
                        onChange={handleFormChange}
                        onBlur={handleBlur}
                        error={Boolean(formErrors.website)}
                        helperText={formErrors.website || ""}
                    />

                    <NavigationButtons>
                        <Button variant="outlined" onClick={newRegistrationStep1} size="large">
                            Back
                        </Button>
                        <Button variant="contained" onClick={newRegistrationStep3} size="large">
                            Continue
                        </Button>
                    </NavigationButtons>
                </StyledCardContent>
            </WideStyledCard>
            <Copyright />
        </StyledContainer>
    );
};

export default RegisterStep2;
